import { createFileRoute } from "@tanstack/react-router";
import { DollarSign, Calculator, Receipt, FileText, Users, ShieldCheck } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/software-de-folha-de-pagamento-para-construcao";
const TITLE = "Software de Folha de Pagamento para Construção — Business Dite Ads";
const DESC =
  "Folha de pagamento para construção civil: pagamento por hora e diária, integração com ponto, contracheques e custo por obra. Teste 14 dias grátis.";

const faqs = [
  {
    q: "Suporta pagamento por hora e por diária?",
    a: "Sim. Cada funcionário pode ser configurado como horista ou diarista. A diária multiplica o valor pelos dias trabalhados; a hora multiplica pelas horas reais.",
  },
  {
    q: "A folha puxa do controle de ponto automaticamente?",
    a: "Sim. As folhas de ponto aprovadas alimentam toda execução da folha — você não redigita horas ou dias e o custo de mão de obra é dividido por obra automaticamente.",
  },
  {
    q: "Os trabalhadores veem o próprio contracheque?",
    a: "Sim. Cada trabalhador tem uma área privada com o detalhamento de horas/dias, valor e bruto.",
  },
  {
    q: "É feito especificamente para construção?",
    a: "Sim. Trata a distribuição de mão de obra entre obras, diárias comuns na construção e liga o custo ao lucro de cada projeto.",
  },
];

export const Route = createFileRoute("/software-de-folha-de-pagamento-para-construcao")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
    ],
    links: [
      { rel: "canonical", href: URL },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://busditeads.com/construction-payroll-software",
      },
      {
        rel: "alternate",
        hrefLang: "es",
        href: "https://busditeads.com/software-de-nomina-para-construccion",
      },
      { rel: "alternate", hrefLang: "pt", href: URL },
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: "https://busditeads.com/construction-payroll-software",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "pt-BR",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Folha de Pagamento para Construção"
      h1="Folha feita para construção — hora, diária, multi-obra"
      subhead="Pague sua equipe com precisão em cada período. Horas e dias vêm do controle de ponto, o custo é dividido por obra e o trabalhador recebe contracheque digital."
      primaryCtaLabel="Começar teste grátis de 14 dias"
      secondaryCtaLabel="Ver preços"
      benefits={[
        "Hora e diária",
        "Controle de ponto incluso",
        "Custo por obra",
        "Cancele quando quiser",
      ]}
      featuresHeading="Folha que entende como a construção paga de verdade"
      features={[
        {
          icon: DollarSign,
          title: "Hora ou diária",
          description:
            "Alterne cada trabalhador entre /hora e /dia. O sistema calcula o bruto pelas horas reais ou dias trabalhados.",
        },
        {
          icon: Calculator,
          title: "Custo automático por obra",
          description:
            "Diárias e horas são divididas proporcionalmente entre as obras em que o trabalhador atuou — para lucro real por projeto.",
        },
        {
          icon: Receipt,
          title: "Contracheques digitais",
          description: "Gere PDFs com horas/dias, valor, bruto e período — para enviar e baixar.",
        },
        {
          icon: FileText,
          title: "Fluxo de aprovação e pagamento",
          description:
            "Revise a folha, confirme cada item, gere contracheques e feche o período. Todas as alterações ficam registradas.",
        },
        {
          icon: Users,
          title: "Funcionários e subs",
          description:
            "Pague registrados e prestadores no mesmo sistema, com permissões por função.",
        },
        {
          icon: ShieldCheck,
          title: "Pronto para auditoria",
          description:
            "Histórico imutável da folha, PDFs de contracheque e exportações para contadores e fechamento fiscal.",
        },
      ]}
      howItWorksHeading="Das horas ao pagamento em três passos"
      steps={[
        {
          title: "Defina o valor por trabalhador",
          description:
            "Configure cada um como horista ou diarista com seu valor. Edite quando quiser — o histórico fica salvo.",
        },
        {
          title: "Puxe as folhas aprovadas",
          description:
            "Inicie uma nova execução de folha — horas aprovadas e dias trabalhados entram automaticamente.",
        },
        {
          title: "Confirme e emita os contracheques",
          description: "Revise, confirme cada item e gere PDFs prontos para enviar.",
        },
      ]}
      socialProofHeading="Por que empreiteiros confiam na nossa folha"
      socialProof={[
        "Rode a folha em minutos, não em um dia inteiro",
        "Acabe com pagar a mais ou a menos — cada minuto é registrado",
        "Saiba o custo exato de mão de obra por obra antes de faturar",
        "Os trabalhadores veem o próprio contracheque a qualquer hora",
        "Suporta diárias comuns na construção residencial",
        "Disponível em português, espanhol e inglês",
      ]}
      faqHeading="Perguntas frequentes"
      faqs={faqs}
      finalCtaHeading="Pare de temer o dia do pagamento."
      finalCtaSubhead="Teste o Business Dite Ads grátis por 14 dias e rode sua próxima folha em minutos."
    />
  );
}
